/**
 * Bulk edit sessions are tracked in localStorage so a process that fails midway
 * can be audited and retried later, even after a refresh.
 *
 * Session shape:
 * {
 *   id, created_at, updated_at,
 *   status: 'draft' | 'running' | 'completed' | 'completed_with_errors' | 'aborted',
 *   collection,   // collection id (see collections.js)
 *   query,        // query context used (or { ids } when loaded from an id list / retry)
 *   query_label,  // human readable description of the query
 *   ids,          // every audience id returned by the query
 *   operations,   // the edit operations applied (see lib/operations.js)
 *   results: {
 *     [id]: { name, status, attempts, errors: [{ message, at }], changes }
 *   }
 * }
 */

const STORAGE_KEY = '@sharethis/bulk-edit-audiences/sessions'
const MAX_SESSIONS = 20

export const RESULT_STATUS = {
  PENDING: 'pending',
  UNCHANGED: 'unchanged',
  // removed from the run by the user on the review stage
  EXCLUDED: 'excluded',
  RUNNING: 'running',
  SUCCESS: 'success',
  ERROR: 'error',
  // same error twice in a row for this audience — excluded from automatic retries
  PERSISTENT_ERROR: 'persistent_error',
  SKIPPED: 'skipped',
}

export const FAILED_STATUSES = [
  RESULT_STATUS.ERROR,
  RESULT_STATUS.PERSISTENT_ERROR,
  RESULT_STATUS.SKIPPED,
]

const readAll = () => {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY)) || []
  } catch (error) {
    return []
  }
}

const writeAll = (sessions) =>
  localStorage.setItem(STORAGE_KEY, JSON.stringify(sessions.slice(0, MAX_SESSIONS)))

export const getSessions = () => readAll()

export function createSession({ collection, query, queryLabel, audiences }) {
  const now = new Date().toISOString()
  const session = {
    id: `bulk-edit-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    created_at: now,
    updated_at: now,
    status: 'draft',
    collection,
    query,
    query_label: queryLabel,
    ids: audiences.map(({ _id }) => _id),
    operations: null,
    results: Object.fromEntries(
      audiences.map(({ _id, name }) => [
        _id,
        { name, status: RESULT_STATUS.PENDING, attempts: 0, errors: [] },
      ])
    ),
  }
  writeAll([session, ...readAll()])
  return session
}

export function saveSession(session) {
  const updated = { ...session, updated_at: new Date().toISOString() }
  writeAll([updated, ...readAll().filter(({ id }) => id !== session.id)])
  return updated
}

export function deleteSession(id) {
  writeAll(readAll().filter((session) => session.id !== id))
}

export const getFailedIds = (session) =>
  Object.entries(session.results)
    .filter(([, result]) => FAILED_STATUSES.includes(result.status))
    .map(([id]) => id)

export const countByStatus = (session) =>
  Object.values(session.results).reduce(
    (counts, { status }) => ({ ...counts, [status]: (counts[status] || 0) + 1 }),
    {}
  )
